import React, { createContext, useContext, useReducer } from 'react';
import { authStorage } from '../utils/storage';
import { authApi, registerUnauthorizedCallback } from '../config/api';
import { authToasts, parseApiError } from '../utils/toast';

// Auth context
const AuthContext = createContext();

// Auth actions
export const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  REGISTER_START: 'REGISTER_START',
  REGISTER_SUCCESS: 'REGISTER_SUCCESS',
  REGISTER_FAILURE: 'REGISTER_FAILURE',
  FORGOT_PASSWORD_START: 'FORGOT_PASSWORD_START',
  FORGOT_PASSWORD_SUCCESS: 'FORGOT_PASSWORD_SUCCESS',
  FORGOT_PASSWORD_FAILURE: 'FORGOT_PASSWORD_FAILURE',
  RESET_PASSWORD_START: 'RESET_PASSWORD_START',
  RESET_PASSWORD_SUCCESS: 'RESET_PASSWORD_SUCCESS',
  RESET_PASSWORD_FAILURE: 'RESET_PASSWORD_FAILURE',
  SET_USER: 'SET_USER',
  CLEAR_ERROR: 'CLEAR_ERROR',
};

// Initial state
const initialState = {
  user: null,
  token: null,
  isLoading: false,
  isAuthenticated: false,
  error: null,
};

// Auth reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
    case AUTH_ACTIONS.REGISTER_START:
    case AUTH_ACTIONS.FORGOT_PASSWORD_START:
    case AUTH_ACTIONS.RESET_PASSWORD_START:
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        isLoading: false,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
        error: null,
      };

    case AUTH_ACTIONS.REGISTER_SUCCESS:
    case AUTH_ACTIONS.FORGOT_PASSWORD_SUCCESS:
    case AUTH_ACTIONS.RESET_PASSWORD_SUCCESS:
      return {
        ...state,
        isLoading: false,
        error: null,
      };

    case AUTH_ACTIONS.LOGIN_FAILURE:
    case AUTH_ACTIONS.REGISTER_FAILURE:
    case AUTH_ACTIONS.FORGOT_PASSWORD_FAILURE:
    case AUTH_ACTIONS.RESET_PASSWORD_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };

    case AUTH_ACTIONS.LOGOUT:
      return {
        ...initialState,
      };

    case AUTH_ACTIONS.SET_USER:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: !!action.payload.token,
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
};

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState, (initial) => {
    const token = authStorage.getToken();
    const user = authStorage.getUser();
    return {
      ...initial,
      user,
      token,
      isAuthenticated: !!token && !!user,
    };
  });

  // Verify token on mount and register unauthorized callback
  React.useEffect(() => {
    // Register callback for 401 Unauthorized responses
    registerUnauthorizedCallback(() => {
      logout();
      authToasts.error('Session expired. Please login again.');
    });

    const verifyToken = async () => {
      const token = authStorage.getToken();
      if (token) {
        try {
          // Use the verifyToken endpoint
          await authApi.verifyToken();
        } catch (error) {
          console.error('Token verification failed:', error);
          // If 401, the callback above will handle it.
        }
      }
    };

    verifyToken();
  }, []);

  // Auth actions
  const login = async (credentials) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });

    // Show loading toast
    // Show loading toast
    authToasts.loginLoading();

    try {
      // Real API call using centralized API config
      const response = await authApi.login(credentials);

      if (response.success) {
        // Store tokens and user
        authStorage.setToken(response.token);
        authStorage.setUser(response.user);
        if (response.refresh) {
          authStorage.setRefreshToken(response.refresh);
        }

        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: {
            user: response.user,
            token: response.token,
          },
        });

        // Show success toast
        authToasts.loginSuccess(response.user.name);

        return {
          success: true,
          user: response.user,
          redirectUrl: response.redirectUrl,
          userType: response.userType,
        };
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error) {
      const errorMessage = parseApiError(error);

      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: errorMessage,
      });

      // Show error toast
      authToasts.loginError(errorMessage);

      return { success: false, error: errorMessage };
    }
  };

  const register = async (userData) => {
    dispatch({ type: AUTH_ACTIONS.REGISTER_START });

    // Show loading toast
    // Show loading toast
    authToasts.registerLoading();

    try {
      // Real API call using centralized API config
      const response = await authApi.register(userData);

      if (response.success) {
        // Store tokens if registration includes auto-login
        if (response.token) {
          authStorage.setToken(response.token);
          authStorage.setUser(response.user);
          if (response.refresh) {
            authStorage.setRefreshToken(response.refresh);
          }

          dispatch({
            type: AUTH_ACTIONS.LOGIN_SUCCESS,
            payload: {
              user: response.user,
              token: response.token,
            },
          });
        } else {
          dispatch({ type: AUTH_ACTIONS.REGISTER_SUCCESS });
        }

        // Show success toast
        authToasts.registerSuccess(response.user.name);

        return {
          success: true,
          user: response.user,
          redirectUrl: response.redirectUrl,
          userType: response.userType,
        };
      } else {
        throw new Error(response.message || 'Registration failed');
      }
    } catch (error) {
      const errorMessage = parseApiError(error);

      dispatch({
        type: AUTH_ACTIONS.REGISTER_FAILURE,
        payload: errorMessage,
      });

      // Show error toast
      authToasts.registerError(errorMessage);

      return { success: false, error: errorMessage };
    }
  };

  const forgotPassword = async (email) => {
    dispatch({ type: AUTH_ACTIONS.FORGOT_PASSWORD_START });

    // Show loading toast
    // Show loading toast
    authToasts.forgotPasswordLoading();

    try {
      // Real API call using centralized API config
      const response = await authApi.forgotPassword(email);

      if (response.success) {
        dispatch({ type: AUTH_ACTIONS.FORGOT_PASSWORD_SUCCESS });

        // Show success toast
        authToasts.forgotPasswordSuccess();

        return {
          success: true,
          otpToken: response.otpToken,
          message: response.message,
        };
      } else {
        throw new Error(response.message || 'Failed to send reset email');
      }
    } catch (error) {
      const errorMessage = parseApiError(error);

      dispatch({
        type: AUTH_ACTIONS.FORGOT_PASSWORD_FAILURE,
        payload: errorMessage,
      });

      // Show error toast
      authToasts.forgotPasswordError(errorMessage);

      return { success: false, error: errorMessage };
    }
  };

  const verifyOtp = async (otpData) => {
    // Show loading toast
    // Show loading toast
    authToasts.otpVerifyLoading();

    try {
      // Real API call using centralized API config
      const response = await authApi.verifyOtp(otpData);

      if (response.success) {
        // Show success toast
        authToasts.otpVerifySuccess();

        return {
          success: true,
          resetToken: response.resetToken,
        };
      } else {
        throw new Error(response.message || 'OTP verification failed');
      }
    } catch (error) {
      const errorMessage = parseApiError(error);

      // Show error toast
      authToasts.otpVerifyError(errorMessage);

      return { success: false, error: errorMessage };
    }
  };

  const resetPassword = async (resetData) => {
    dispatch({ type: AUTH_ACTIONS.RESET_PASSWORD_START });

    // Show loading toast
    // Show loading toast
    authToasts.passwordResetLoading();

    try {
      // Real API call using centralized API config
      const response = await authApi.resetPassword(resetData);

      if (response.success) {
        dispatch({ type: AUTH_ACTIONS.RESET_PASSWORD_SUCCESS });

        // Show success toast
        authToasts.passwordResetSuccess();

        return { success: true, message: response.message };
      } else {
        throw new Error(response.message || 'Failed to reset password');
      }
    } catch (error) {
      const errorMessage = parseApiError(error);

      dispatch({
        type: AUTH_ACTIONS.RESET_PASSWORD_FAILURE,
        payload: errorMessage,
      });

      // Show error toast
      authToasts.passwordResetError(errorMessage);

      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    authStorage.clearAll();
    dispatch({ type: AUTH_ACTIONS.LOGOUT });

    // Show logout success toast
    authToasts.logoutSuccess();
  };

  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  const value = {
    ...state,
    login,
    register,
    forgotPassword,
    verifyOtp,
    resetPassword,
    logout,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
